import { useEffect, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { SNAPSHOT_CONFIGS } from "./experience";
import { LOGO_URL } from "./data";

// Printable Snapshot page.
// Open /print/<slug> in a browser -> Cmd/Ctrl+P -> Save as PDF.

const PRINT_PASSWORD = "2462";
const UNLOCK_KEY = "sf_print_unlocked_v2";

/**
 * Lotus icon in inline SVG.
 * @param color - hex accent color from the petal
 * @param size - width in px
 * @param opacity - 0..1
 * @param className - optional extra class (used for watermark)
 */
function LotusIcon({ color, size = 64, opacity = 1, className = "" }) {
  const s = { color };
  return (
    <svg
      viewBox="0 0 120 90"
      width={size}
      height={size * 90 / 120}
      className={`print-lotus ${className}`}
      style={{ opacity, ...s }}
      aria-hidden="true"
    >
      {/* Base water line */}
      <path
        d="M15 78 Q 60 88 105 78"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
        opacity="0.55"
      />
      {/* Outer left petal */}
      <path
        d="M60 78 C 30 68 12 46 22 32 C 30 42 42 58 60 78 Z"
        fill="currentColor"
        opacity="0.35"
      />
      {/* Outer right petal */}
      <path
        d="M60 78 C 90 68 108 46 98 32 C 90 42 78 58 60 78 Z"
        fill="currentColor"
        opacity="0.35"
      />
      {/* Middle left petal */}
      <path
        d="M60 78 C 46 62 34 40 42 20 C 50 30 56 50 60 78 Z"
        fill="currentColor"
        opacity="0.6"
      />
      {/* Middle right petal */}
      <path
        d="M60 78 C 74 62 86 40 78 20 C 70 30 64 50 60 78 Z"
        fill="currentColor"
        opacity="0.6"
      />
      {/* Center petal */}
      <path
        d="M60 78 C 54 60 54 32 60 8 C 66 32 66 60 60 78 Z"
        fill="currentColor"
        opacity="0.9"
      />
      {/* Center highlight */}
      <ellipse cx="60" cy="72" rx="4" ry="2" fill="currentColor" opacity="0.75" />
    </svg>
  );
}

function WriteLines({ count = 4 }) {
  return (
    <div className="print-lines" aria-hidden="true">
      {Array.from({ length: count }).map((_, i) => <div className="print-line" key={i} />)}
    </div>
  );
}

function Prompt({ p, idx, accent }) {
  const main = p.mainQ || p.think;
  const cue = p.mainQ && p.selfie ? p.selfie : null;
  return (
    <div
      className="print-prompt"
      data-testid={`print-prompt-${idx}`}
      style={{ borderLeftColor: accent }}
    >
      <div className="print-prompt-header">
        <span className="print-prompt-num" style={{ color: accent }}>{idx + 1}.</span>
        {p.label && <span className="print-prompt-label" style={{ color: accent }}>{p.label}</span>}
      </div>
      {main && <p className="print-prompt-main">{main}</p>}
      {cue && <p className="print-prompt-cue">{cue}</p>}
      {p.q1 && (
        <div className="print-q">
          <div className="print-q-label">{p.q1}</div>
          <WriteLines count={4} />
        </div>
      )}
      {p.q2 && (
        <div className="print-q">
          <div className="print-q-label">{p.q2}</div>
          <WriteLines count={4} />
        </div>
      )}
      {p.q3 && (
        <div className="print-q">
          <div className="print-q-label">{p.q3}</div>
          <WriteLines count={4} />
        </div>
      )}
    </div>
  );
}

function Petal({ petal, index, total }) {
  const name = petal.name || petal.color;
  const accent = petal.accent || "#B58B2E";
  return (
    <section className="print-petal print-page-break" data-testid={`print-petal-${index}`}>
      <div
        className="print-petal-header"
        style={{ borderBottomColor: accent }}
      >
        {/* Soft lotus watermark behind title */}
        <div className="print-lotus-watermark" aria-hidden="true">
          <LotusIcon color={accent} size={220} opacity={0.12} />
        </div>
        <div className="print-petal-index" style={{ color: accent }}>Petal {index + 1} of {total}</div>
        {/* Small lotus icon above title */}
        <div className="print-petal-lotus" data-testid={`print-petal-lotus-${index}`}>
          <LotusIcon color={accent} size={64} opacity={0.85} />
        </div>
        <h2 className="print-petal-name" style={{ color: accent }} data-testid={`print-petal-name-${index}`}>{name}</h2>
        <div className="print-petal-theme">{petal.theme}</div>
      </div>
      <div className="print-petal-body">
        {petal.prompts.map((p, i) => (
          <Prompt key={i} p={p} idx={i} accent={accent} />
        ))}
      </div>
    </section>
  );
}

export default function PrintExperience() {
  const { slug } = useParams();
  const config = SNAPSHOT_CONFIGS[slug];
  const [unlocked, setUnlocked] = useState(() => {
    try { return localStorage.getItem(UNLOCK_KEY) === "1"; } catch { return false; }
  });
  const [entered, setEntered] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    document.title = config ? `${config.brand} Snapshot — Printable | SnapshotFame` : "Snapshot Print";
    document.body.classList.add("print-mode");
    return () => document.body.classList.remove("print-mode");
  }, [config]);

  if (!config) return <Navigate to="/" replace />;

  const totalPetals = config.petals.length;

  const tryUnlock = (e) => {
    e.preventDefault();
    if (entered.trim() === PRINT_PASSWORD) {
      try { localStorage.setItem(UNLOCK_KEY, "1"); } catch {}
      setUnlocked(true);
      setErrorMsg("");
    } else {
      setErrorMsg("That doesn't look right. Please check with Dawn for the correct code.");
    }
  };

  if (!unlocked) {
    return (
      <div className="print-gate" data-testid="print-gate">
        <div className="print-gate-card">
          <img src={LOGO_URL} alt="SnapshotFame" className="print-gate-logo" />
          <div className="print-gate-brand">SNAPSHOTFAME · LIFE CLARITY CHIC</div>
          <h1 className="print-gate-title">Printable {config.brand} Snapshot</h1>
          <p className="print-gate-sub">This booklet is private. Please enter the friends &amp; family code Dawn shared with you.</p>
          <form onSubmit={tryUnlock} className="print-gate-form">
            <input
              type="password"
              inputMode="numeric"
              value={entered}
              onChange={(e) => { setEntered(e.target.value); setErrorMsg(""); }}
              placeholder="Enter code"
              className="print-gate-input"
              autoFocus
              data-testid="print-gate-input"
            />
            <button type="submit" className="print-gate-btn" data-testid="print-gate-submit">Unlock booklet</button>
          </form>
          {errorMsg && <p className="print-gate-error" data-testid="print-gate-error">{errorMsg}</p>}

          <div className="print-gate-divider" aria-hidden="true">
            <span>OR</span>
          </div>

          <p className="print-gate-purchase-lead">
            No code yet? You can experience the full <em>{config.brand} Snapshot</em> right now.
          </p>
          <Link
            to={`/snapshots/${slug}`}
            className="print-gate-purchase-btn"
            data-testid="print-gate-purchase"
          >
            Get Your Snapshot →
          </Link>

          <p className="print-gate-footer">
            Already purchased? <Link to={`/experience/${slug}`} className="print-gate-footer-link" data-testid="print-gate-existing">Enter your access code</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="print-root" data-testid="print-root">
      {/* Cover */}
      <section className="print-cover" data-testid="print-cover">
        <img src={LOGO_URL} alt="SnapshotFame" className="print-logo" />
        <div className="print-brand-line">SNAPSHOTFAME · LIFE CLARITY CHIC</div>
        <h1 className="print-cover-title">{config.brand}<br /><span className="print-cover-sub">Snapshot</span></h1>
        <p className="print-cover-quote">&ldquo;{config.welcomeQuote}&rdquo;</p>
        <div className="print-cover-body">
          {config.welcomeBody.map((b, i) => (
            <p key={i}>{b}</p>
          ))}
        </div>
        <div className="print-cover-note">
          <strong>How to use this booklet:</strong> Read each prompt slowly. Trust the first thing that comes.
          Write your Memory and your Meaning. When a petal is finished, pause. Read your own words back to
          yourself before moving on.
        </div>
        <div className="print-cover-note italic">
          There are no right or wrong answers — only honest ones.
        </div>
        <div className="print-cover-footer">
          A private reflection experience by Dawn Delisle · snapshotfame.com
        </div>
      </section>

      {/* Petals */}
      {config.petals.map((petal, i) => (
        <Petal key={i} petal={petal} index={i} total={totalPetals} />
      ))}

      {/* Closing — Look for Your Loops */}
      <section className="print-loops print-page-break" data-testid="print-loops">
        <div className="print-loops-label">FINAL REFLECTION</div>
        <h2 className="print-loops-title">Look for Your Loops</h2>
        <p className="print-loops-lead">
          Read your answers across all {totalPetals} {totalPetals === 1 ? "petal" : "petals"} back to yourself.
          The answers you gave are only half the picture. The other half is what repeats.
        </p>
        <div className="print-loops-grid">
          {[
            "Words or phrases that came up in more than one petal",
            "Emotions that kept surfacing",
            "Beliefs about yourself, others, or the world",
            "Body sensations or where you felt things",
            "Images, colours, or places that returned",
            "Themes — safety, worth, control, freedom, love, fear, longing",
          ].map((line, i) => (
            <div className="print-loop-item" key={i}>
              <span className="print-loop-bullet">◆</span>
              <div>
                <div className="print-loop-line">{line}</div>
                <WriteLines count={2} />
              </div>
            </div>
          ))}
        </div>

        <div className="print-loops-close">
          <div className="print-q">
            <div className="print-q-label">One pattern I now see clearly:</div>
            <WriteLines count={4} />
          </div>
          <div className="print-q">
            <div className="print-q-label">One thing this pattern has been trying to show me:</div>
            <WriteLines count={4} />
          </div>
          <div className="print-q">
            <div className="print-q-label">One small honest step I can take because of what I saw:</div>
            <WriteLines count={4} />
          </div>
        </div>

        <div className="print-loops-cta">
          <p><strong>Want Dawn to help you connect the pieces?</strong></p>
          <p>Book a private session at <em>snapshotfame.com</em></p>
        </div>
      </section>

      {/* Screen-only helper: print button */}
      <div className="print-screen-only" data-testid="print-toolbar">
        <button className="print-download-btn" onClick={() => window.print()} data-testid="print-download-btn">
          Save as PDF
        </button>
        <div className="print-help">
          On Mac press <kbd>⌘</kbd> + <kbd>P</kbd> · On PC press <kbd>Ctrl</kbd> + <kbd>P</kbd> · then choose <em>Save as PDF</em>.
        </div>
      </div>
    </div>
  );
}
